import {Button, Group, Loader, Paper, Tooltip} from '@mantine/core';
import {useMachine} from '@xstate/react';
import React, {useEffect, useMemo, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {AnalyticsModalType} from 'analytics/AnalyticsTypes';
import {
  FullScreenFlow,
  FullScreenFlowContent,
  FullScreenFlowFooter,
  FullScreenFlowHeader,
} from 'antibodies/nested-data-creation/full-screen/FullScreenFlowLayout';
import ProteinCreationFinalizeStep from 'antibodies/nested-data-creation/full-screen/ProteinCreationFinalizeStep';
import ProteinCreationFlowMachine from 'antibodies/nested-data-creation/full-screen/ProteinCreationFlowMachine';
import ProteinCreationTable from 'antibodies/nested-data-creation/full-screen/ProteinCreationTable';
import {
  buildNdcInterpretationFromRows,
  mergeResolvedEntitiesIntoRows,
  tableHasData,
} from 'antibodies/nested-data-creation/full-screen/ProteinCreationTableUtil';
import useProteinCreationTableState from 'antibodies/nested-data-creation/full-screen/useProteinCreationTableState';
import {
  guardRelatedSchemasConfigIsValid,
  guardSelectedProjectFolderIsValid,
} from 'antibodies/nested-data-creation/ProteinCreationSharedUtil';
import {columnsIncludeDnaType} from 'antibodies/nested-data-creation/spreadsheet-import/AntibodyNestedDataCreationSpreadsheetImportUtil';
import ValidationErrorsPane from 'antibodies/nested-data-creation/spreadsheet-import/panes/ValidationErrorsPane';
import {ANALYTICS_MODULE, COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import navigateToURL from 'lib/navigateToURL';
import * as FullScreenActions from 'nav/full-screen/FullScreenActions';
import hasBlockingValidationErrors from 'notebook/structured-tables/util/hasBlockingValidationErrors';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import notifications from 'notifications';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import * as logging from 'util/Logging';

function ProteinCreationFlowPage(): JSX.Element {
  const [state, send] = useMachine(ProteinCreationFlowMachine, {input: {}});
  const {creationSettings} = state.context;
  const tableState = useProteinCreationTableState(creationSettings);

  const [computedTable, setComputedTable] = useState<Array<Array<EvaluatedCell>> | null>(null);

  // Merge table rows with computed values to include resolved entities
  const rows = useMemo(() => {
    const tableRows = tableState?.rows;
    const tableColumns = tableState?.structuredTable.columns;
    if (tableRows == null || tableColumns == null) {
      return [];
    }
    return mergeResolvedEntitiesIntoRows(tableRows, tableColumns, computedTable);
  }, [tableState?.rows, tableState?.structuredTable.columns, computedTable]);
  const hasData = tableHasData(rows);
  const hasBlockingErrors = computedTable != null && hasBlockingValidationErrors(computedTable);
  const canAdvanceToFinalize = hasData && !hasBlockingErrors;
  const interpretation = buildNdcInterpretationFromRows(
    rows,
    creationSettings.selectedFormat,
    creationSettings.selectedStartingType
  );
  const hasDnaColumns = columnsIncludeDnaType(interpretation.columns);
  const numberOfAntibodies = interpretation.rowCount;

  useEffect(() => {
    if (!state.matches('done')) {
      return;
    }
    const {ndcWorkspaceUrl} = state.context;
    if (ndcWorkspaceUrl != null) {
      notifications.info('Open the flowchart to view progress.', {
        actionText: 'View flowchart',
        onActionClick: () => {
          navigateToURL(ndcWorkspaceUrl);
        },
      });
    } else {
      logging.error('No NDC workspace URL returned from create proteins from table');
      notifications.info("We're working on creating proteins and components...");
    }
    analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL, ANALYTICS_MODULE.ANTIBODIES, {
      modalType: AnalyticsModalType.ANTIBODY_NESTED_DATA_CREATION,
      fullScreen: true,
      formatId: state.context.creationSettings.selectedFormat.id,
      startingDataType: state.context.creationSettings.selectedStartingType,
      numberOfAntibodies,
    });
    FullScreenActions.hideScreenForRouting();
  }, [state, numberOfAntibodies]);

  const getNextButtonTooltip = (): string | undefined => {
    if (!hasData) {
      return 'Add at least one protein to continue';
    }
    if (hasBlockingErrors) {
      return 'Fix errors to continue';
    }
    return undefined;
  };

  return (
    <FullScreenFlow>
      <FullScreenFlowHeader
        title="Create proteins"
        subtitle={creationSettings.selectedSchemaName}
        onClose={() => {
          FullScreenActions.hideScreenForRouting();
        }}
      />
      <FullScreenFlowContent>
        {state.matches('initializing') && <Loader />}
        {state.matches('editing') &&
          (tableState != null ? (
            <ProteinCreationTable
              format={creationSettings.selectedFormat}
              startingType={creationSettings.selectedStartingType}
              structuredTable={tableState.structuredTable}
              handleTableUpdate={tableState.handleTableUpdate}
              replaceRows={tableState.replaceRows}
              isSchemaLoaded={tableState.isSchemaLoaded}
              computedTable={computedTable}
              onComputedTableChanged={setComputedTable}
            />
          ) : (
            <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load protein creation table" />
          ))}
        {state.matches('finalize') && (
          <ProteinCreationFinalizeStep
            rows={rows}
            creationSettings={creationSettings}
            projectFolderId={state.context.projectFolderId}
            onSetProjectFolder={(id) => {
              send({type: 'setProjectFolder', projectFolderId: id});
            }}
            relatedSchemasConfig={state.context.relatedComponentSchemas.selected}
            onSetRelatedSchemasConfig={(config) => {
              send({type: 'setRelatedSchemasConfig', relatedSchemasConfig: config});
            }}
            translationParameters={state.context.translationParameters}
            onSetTranslationParameters={(params) => {
              send({type: 'setTranslationParameters', translationParameters: params});
            }}
            componentAndRelatedSchemas={state.context.relatedComponentSchemas.available}
            componentAndRelatedSchemasErrorMessage={state.context.relatedComponentSchemas.errorMessage}
            selectedDocumentId={state.context.selectedDocumentId}
            onSetSelectedDocumentId={(id) => {
              send({type: 'setSelectedDocumentId', documentId: id});
            }}
          />
        )}
        {state.matches('validationErrors') && state.context.validationErrors != null && (
          <Paper bg="white" radius="md" p="md">
            <ValidationErrorsPane
              submissionValidationErrors={{
                validationErrors: state.context.validationErrors.rowErrors,
                batchErrors: state.context.validationErrors.batchErrors,
              }}
              rowIndexDisplayOffset={1}
            />
          </Paper>
        )}
      </FullScreenFlowContent>
      <FullScreenFlowFooter>
        <Group position="right">
          {state.matches('editing') && (
            <Tooltip label={getNextButtonTooltip()} disabled={canAdvanceToFinalize}>
              <span>
                <Button
                  onClick={() => {
                    send({type: 'next', hasData, hasBlockingErrors});
                  }}
                  disabled={!canAdvanceToFinalize}
                >
                  Next
                </Button>
              </span>
            </Tooltip>
          )}
          {state.matches('finalize') && (
            <>
              <Button
                variant="tertiary"
                onClick={() => {
                  send({type: 'back'});
                }}
              >
                Back
              </Button>
              <Button
                disabled={
                  !guardRelatedSchemasConfigIsValid(
                    state.context.relatedComponentSchemas.selected,
                    hasDnaColumns
                  ) || !guardSelectedProjectFolderIsValid(state.context.projectFolderId)
                }
                onClick={() => {
                  send({
                    type: 'submit',
                    rows,
                    hasDnaColumns,
                  });
                }}
              >
                Submit
              </Button>
            </>
          )}
          {state.matches('validationErrors') && (
            <Button
              variant="tertiary"
              onClick={() => {
                send({type: 'backToEditing'});
              }}
            >
              Back
            </Button>
          )}
        </Group>
      </FullScreenFlowFooter>
    </FullScreenFlow>
  );
}

export default ProteinCreationFlowPage;
